import { useState } from 'react';
import { Plus, Filter, CheckCircle2, Clock, Calendar, AlertCircle, GitCompare, ExternalLink, Eye } from 'lucide-react';
import PlanCard from './PlanCard';
import BenefitsComparison from './BenefitsComparison';
import AddOnPackages from './AddOnPackages';
import { Button } from '@shared/ui/button';
import { toast } from "@shared/ui/use-toast";
import { Alert, AlertTitle, AlertDescription } from '@shared/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@shared/ui/dialog';
import { Badge } from '@shared/ui/badge';
import { format, addDays } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useCurrentSubscription, usePlanSubscription, useTrackRequests } from '../hooks';

const Subscriptions = () => {
  const [activePlan, setActivePlan] = useState<any | null>(null);
  const [activationDate, setActivationDate] = useState<Date | null>(null);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [showSupportRequests, setShowSupportRequests] = useState(false);

  const {
    data: planSubscriptionData,
    isLoading,
    error,
  } = usePlanSubscription();

  console.log('planSubscriptionData', planSubscriptionData)

  const { data: currentSubscription } = useCurrentSubscription();
  const { data: supportRequests, refetch: refetchRequests } = useTrackRequests();
  const getDaysLeft = (endDate: any, startDate: any = new Date()) => {
    const end = new Date(endDate);
    const start = new Date(startDate);

    // Invalid date handling
    if (isNaN(end.getTime()) || isNaN(start.getTime())) return 0;

    const diffInMs = end.getTime() - start.getTime();
    const daysLeft = Math.max(0, Math.ceil(diffInMs / (1000 * 60 * 60 * 24)));

    return daysLeft;
  };
  // Trial information with actual dates
  const trialStartDate = currentSubscription?.startDate; // October 15, 2023
  const trialEndDate = currentSubscription?.endDate;
  const trialDaysLeft = getDaysLeft(trialEndDate, trialStartDate);


  const handleContactSales = () => {
    toast({
      title: "Support Request Submitted",
      description: "Your request has been submitted successfully. You can track its progress using the 'Track Requests' button.",
    });
  };

  const handleEndTrial = () => {
    // toast({
    //   title: "Trial Ended",
    //   description: "Your trial has ended. Please activate a subscription to continue using Smart360."
    // });
  };

  const handleView = (id: string) => {
    console.log(`View plan details for: ${id}`);
  };

  const navigate = useNavigate();

  const handleComparePlans = () => {
    navigate('/plan-comparison');
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status.toLowerCase()) {
      case 'in progress':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'resolved':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPriorityBadgeClass = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'high':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'medium':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'low':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="page-container">
      {/* <div className="breadcrumb animate-fade-in-down">
          <a href="/" className="breadcrumb-item">Dashboard</a>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">Subscription</span>
        </div> */}

      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div className="mb-4 md:mb-0">
          <h1 className="page-title">Manage Your Subscription</h1>
          <p className="page-subtitle">Choose the plan that's right for your utility</p>
        </div>
        <Button
          onClick={() => setShowSupportRequests(true)}
          variant="outline"
          className="flex items-center gap-2"
        >
          <Eye size={16} />
          Track Requests
        </Button>
      </div>

      {currentSubscription?.trialInfo?.isTrial && (
        <Alert className="mb-6 bg-blue-50 border border-blue-200 animate-fade-in">
          <Clock className="h-4 w-4 text-blue-600" />
          <AlertTitle className="text-blue-800">Your Smart360 Trial</AlertTitle>
          <AlertDescription className="text-blue-600">
            <div className="flex flex-col gap-2">
              <p>Your trial started on <span className="font-medium">{trialStartDate.toString()}</span> and will end on <span className="font-medium">{trialEndDate.toString()}</span>.</p>                <p><span className="font-medium">{trialDaysLeft} days remaining</span>. Please select a subscription below to continue using Smart360 after your trial period.</p>
            </div>
          </AlertDescription>
          <Button
            variant="outline"
            className="mt-2 border-blue-300 text-blue-700 hover:bg-blue-100"
            onClick={handleEndTrial}
          >
            End Trial Early
          </Button>
        </Alert>
      )}

      {currentSubscription?.subscriptionPlan?.isActive && !currentSubscription?.trialInfo?.isTrial && (
        <Alert className="mb-6 bg-green-50 border border-green-200 animate-fade-in">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertTitle className="text-green-800">Active Subscription</AlertTitle>
          <AlertDescription className="text-green-600">
            <p>You have an active subscription to <strong>{currentSubscription?.subscriptionPlan?.subscriptionName}</strong>.</p>
            {currentSubscription?.startDate && (
              <p className="mt-1">Activated on: <strong>{currentSubscription?.startDate}</strong></p>
            )}
          </AlertDescription>
        </Alert>
      )}

      {!currentSubscription?.trialInfo?.isTrial && !currentSubscription?.subscriptionPlan?.isActive && (
        <Alert className="mb-6 bg-amber-50 border border-amber-200 animate-fade-in">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-800">Subscription Required</AlertTitle>
          <AlertDescription className="text-amber-600">
            Your trial has ended. Please select a subscription below to continue using Smart360.
          </AlertDescription>
        </Alert>
      )}

      <div className="mb-6">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Choose Your Subscription Plan</h2>
          <p className="text-gray-600">Select the plan that best fits your utility's needs</p>
        </div>

        {/* Enhanced Billing Cycle Container with Plan Cards */}
        <div className="rounded-md p-6 border-2 animate-fade-in">
          <div className="text-center mb-4">
            <p className="text-gray-600">Save up to 17% with yearly billing</p>
          </div>

          <div className="flex justify-center mb-6">
            <div className="flex items-center bg-white rounded-xl p-2 shadow-md border border-gray-200">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-6 py-3 rounded-lg text-sm font-semibold transition-all duration-300 ${billingCycle === 'monthly'
                    ? 'bg-blue-500 text-white shadow-lg transform scale-105'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
              >
                Monthly Billing
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`px-6 py-3 rounded-lg text-sm font-semibold transition-all duration-300 relative flex items-center gap-2 ${billingCycle === 'yearly'
                    ? 'bg-green-500 text-white shadow-lg transform scale-105'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
              >
                Yearly Billing
                <span className="bg-gradient-to-r from-orange-400 to-red-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-sm animate-pulse">
                  SAVE 17%
                </span>
              </button>
            </div>
          </div>

          {billingCycle === 'yearly' && (
            <div className="mb-6 text-center animate-fade-in">
              <div className="inline-flex items-center bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-medium">
                <span className="mr-2">🎉</span>
                You're saving money with yearly billing!
                <span className="ml-2">💰</span>
              </div>
            </div>
          )}

          {billingCycle === 'yearly' && (
            <div className="text-center mb-6">
              <div className="inline-flex items-center bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-medium">
                ⭐ Yearly plans include 2 months free!
              </div>
            </div>
          )}

          {/* Plan Cards Grid - Now inside the billing cycle container */}
          {planSubscriptionData?.result?.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 animate-fade-in">
              {planSubscriptionData.result.map((plan) => (
                <PlanCard
                  key={plan?.id}
                  type={billingCycle === 'yearly' ? "yearly" : "monthly"}
                  title={plan?.subscriptionName}
                  pricePerMeter={
                    billingCycle === 'monthly'
                      ? plan?.monthly?.baseRate
                      : plan?.yearly?.baseRate
                  }
                  meterCount={plan?.meterLimit}
                  monthlyCost={
                    billingCycle === 'monthly'
                      ? plan?.monthly?.totalCost
                      : plan?.yearly?.totalCost
                  }
                  billingCycle={billingCycle}
                  status={
                    activePlan === plan?.id
                      ? 'active'
                      : currentSubscription?.trialInfo?.isTrial
                        ? 'trial'
                        : 'inactive'
                  }
                  onEdit={undefined}
                  onContactSales={() => refetchRequests()}
                  showContactSales
                  keyDifferentiators={plan?.keyFeatures}
                  features={[]}
                  meterDescription={plan?.meterLimit}
                />
              ))}
            </div>
          )}

          {/* Explore Add-ons Button - Positioned right after plans for better visibility */}
          <div className="text-center mt-8">
            <Button
              onClick={() => document.getElementById('addons-section')?.scrollIntoView({ behavior: 'smooth' })}
              variant="outline"
              size="lg"
              className="bg-gradient-to-r from-blue-50 to-green-50 border-blue-300 text-blue-700 hover:bg-blue-100 font-semibold shadow-md"
            >
              🔧 Explore Add-On Packages
            </Button>
          </div>
        </div>
      </div>

      {/* Plan Comparison Section */}
      <BenefitsComparison />

      {/* Add-On Packages Section */}
      <div id="addons-section" className="mt-12">
        <AddOnPackages refetchSupportRequests={refetchRequests} />
      </div>

      {/* Support Requests Dialog */}
      <Dialog open={showSupportRequests} onOpenChange={setShowSupportRequests}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye size={20} />
              Track Support Requests
            </DialogTitle>
            <DialogDescription>
              Monitor the status and progress of your sales inquiries and support requests
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {supportRequests?.length > 0 ? (
              supportRequests?.map((request) => (
                <div key={request.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-lg">{request.title}</h3>
                        <Badge variant="outline" className="text-xs font-medium">
                          {request.externalTicketId}
                        </Badge>
                      </div>
                      <p className="text-gray-600 text-sm mb-2">{request.description}</p>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span>Created: {request.createdDate}</span>
                      </div>
                    </div>
                    {/* <div className="flex flex-col items-end gap-2">
                        <Badge className={getStatusBadgeClass(request.status)}>
                          {request.status}
                        </Badge>
                        <Badge className={getPriorityBadgeClass(request.priority)}>
                          {request.priority}
                        </Badge>
                      </div> */}
                  </div>
                  <div className="flex justify-between items-center">
                    <p className='text-sm text-gray-500'>Requested By: {request.requestedBy}</p>

                    <Button
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-2"
                      onClick={() => window.open(request.redirectUrl, '_blank')}
                    >
                      <ExternalLink size={16} />
                      Track Progress
                    </Button>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <div className="text-gray-400 mb-2">
                  <Eye size={48} className="mx-auto" />
                </div>
                <h3 className="text-lg font-medium text-gray-600 mb-1">No Support Requests</h3>
                <p className="text-gray-500">You haven't submitted any support requests yet.</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Subscriptions;